import i18n from "@/Plugins/i18n";

import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import type { ImageChatItemMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import type { ImageChatRoomItem } from "@/Modules/Chat/Models/Domain/Chat";
import type { UserStore } from "@/Modules/User/Store";
import type { ChatStore } from "@/Modules/Chat/Store";
import { ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";
import { ChatRoomItemTypeMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";

import { useUserStore } from "@/Modules/User/Store";
import { useChatStore } from "@/Modules/Chat/Store";
import useAppStore, { type AppStore } from "@/Modules/App/Store";

import { APP_ROUTES } from "@/Generated/Routes/paths";

import { addNewEventWithNeutrals } from "@/Modules/Chat/MessageFilters/helpers/AddNeutralDateEvent";
import { hasValue, isObject } from "@/Utils/Objects";
import { unixSecondsToDate } from "@/Utils/Date";

export interface ExtraParamsImageChatMessageItem {
  chatStore: ChatStore;
  userStore: UserStore;
  appStore: AppStore;
}

export const isImageChatMessageItem = (message: unknown): boolean => {
  return (
    isObject(message) && hasValue(message.etype) && message.etype === ChatRoomItemTypeMessageWebSocket.IMAGE
  );
};

export const imageChatMessageItemHandler = (
  message: ImageChatItemMessageWebSocket,
  {
    chatStore = useChatStore(),
    userStore = useUserStore(),
    appStore = useAppStore(),
  }: ExtraParamsImageChatMessageItem
) => {
  const imageChatRoomItem = transformImageChatMessageWebSocketToImageChatRoomItem(
    message,
    userStore.user?.id
  );

  if (hasValue(chatStore.rooms[message.chat])) {
    const events = addNewEventWithNeutrals(chatStore.rooms[message.chat]!.events, imageChatRoomItem);

    chatStore.rooms[message.chat]!.events = events;

    if (!message.old) {
      appStore.notificate({
        title: chatStore.rooms[message.chat]!.user.username,
        body: i18n.global.t("browser-notifications.new_image"),
        redirect: userStore.getUserIsTherapist ? { name: APP_ROUTES.chat.root.name } : undefined,
        extraParams: chatStore.rooms[message.chat]!.user.id,
      });
    }
  }

  return Promise.resolve(imageChatRoomItem);
};

const transformImageChatMessageWebSocketToImageChatRoomItem = (
  imageChatItemMessageWebSocket: ImageChatItemMessageWebSocket,
  userId = ""
): ImageChatRoomItem => {
  const isMine = userId === imageChatItemMessageWebSocket.user;
  const type = isMine ? ChatRoomItemType.OUTGOING_TEXT : ChatRoomItemType.INCOMING_TEXT;
  const createdAt = unixSecondsToDate(imageChatItemMessageWebSocket.created);
  const imageChatRoomItem: ImageChatRoomItem = {
    id: imageChatItemMessageWebSocket.eid,
    createdAt,
    thumbnail: imageChatItemMessageWebSocket.thumbnail,
    url: imageChatItemMessageWebSocket.url,
    type,
    old: !!imageChatItemMessageWebSocket.old,
    isReceived: false,
    height: imageChatItemMessageWebSocket.height,
    width: imageChatItemMessageWebSocket.width,
  };

  if (type === ChatRoomItemType.OUTGOING_TEXT) {
    imageChatRoomItem.isConfirmed = true;
  }

  return imageChatRoomItem;
};
export default {
  isType: isImageChatMessageItem,
  handler: imageChatMessageItemHandler,
} as EventFilter<ExtraParamsImageChatMessageItem>;
